/* eslint-disable no-param-reassign */
import BasePresenter from '@src/js/separate-logic/base-presenter';
import { AttachmentUploadListener } from '@src/js/common/ark-extensions/dc-components/attachment-uploader/type';
import AttachmentUploader from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import { DefaultFamily } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/font-utils';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import type { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IReactionDisposer } from 'mobx';
import { dispatchProps } from '@src/js/separate-logic/decorate/dispatchProps';
import { isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import { getUploadingMediaAttachmentId } from '@src/slide/application/service/attachment-upload/attachment-manager';

export default class AttachmentPresenter extends BasePresenter implements AttachmentUploadListener {
    private hasShowFirst = false;  // 是否第一次显示出来
    private selectedElementReaction: IReactionDisposer;
    /**
     * 上传组件初始化完成的回调
     */
    public onUpLoaderInit() {
        this.forceUpdate();
    }

    public componentDidMount() {
        const uploader = AttachmentUploader.getInstance();
        const attachmentManager = this.getAttachmentManager();
        if (this.props?.attachment?.objKey) {  // 上传完成才注册监听
            uploader.addUploadListener(this);
        }

        const container: HTMLElement | null = this.attachmentContainer;
        const attachmentId = this.props.attachment?.id;

        if (container && (this.isUploading(attachmentId) || this.isUploadError())) {
            this.delegateEventsToParent();
            this.hasShowFirst = true;
            const linkStyles = { paddingTop: 1, textDecoration: 'none', fontFamily: DefaultFamily };
            uploader.showAttachment(
                attachmentId,
                container,
                {
                    id: attachmentId,
                    styles: { '.text-link': linkStyles },
                    showType:
                        attachmentManager.generateUploadingAttachmentShowType(this.props.attachment.classify).showType,
                },
            );
        }
    }

    public componentWillUpdate(nextProps, nextModel): void {
        const { attachment } = nextProps;

        if (attachment?.objKey) {
            const uploader = AttachmentUploader.getInstance();
            attachment.id && uploader.removeAttachment(attachment.id);
            nextModel.AttachmentComponent = uploader.getAttachmentComponent();
            if (nextModel.AttachmentComponent && !this.hasShowFirst) {
                this.props?.onAttachmentFirstShow?.();
                this.hasShowFirst = true;
            }
        } else {
            nextModel.AttachmentComponent = null;
        }
    }

    public componentWillUnmount(): void {
        const uploader = AttachmentUploader.getInstance();
        uploader.removeUploadListener(this);
        this.removeSelectedElementReaction();
        this.removeContainerEvent();
    }

    /**
     * 判断该附件是否正在上传
     * @param id
     */
    @dispatchProps()
    public isUploading(id: string = this.props.attachment.id) {
        const loadingMap = this.props.uploadingMap ? this.props.uploadingMap
            : this.getAttachmentManager()?.uploadingMap;
        if (loadingMap) {
            if (isVideoElement(this.props.uiModel)) {
                const uploadingVideoAttachmentId = getUploadingMediaAttachmentId(this.props.uiModel);
                return loadingMap.has(uploadingVideoAttachmentId);
            }
            return loadingMap.has(id);
        }
        return false;
    }

    /**
     * 判断该附件是否上传失败
     */
    @dispatchProps()
    public isUploadError() {
        const { uiModel } = this.props;
        return this.getAttachmentManager()?.isUploadError(uiModel.id);
    }

    @dispatchProps()
    public setIsVideoFullScreen = (isVideoFullScreen: boolean) => {
        this.setModel({ isVideoFullScreen });
    };

    /**
     * 获取本地附件信息（目前只有视频支持）
     */
    @dispatchProps()
    private getLocalAttachment() {
        const { uiModel } = this.props;
        return this.getAttachmentManager()?.getAvailableLocalAttachment(uiModel.id as string);
    }

    private removeSelectedElementReaction() {
        if (this.selectedElementReaction) {
            this.selectedElementReaction();
        }
    }

    private get attachmentContainer() {
        return this.getChildRef('attachmentContainer');
    }

    /**
     * 上传过程中的附件元素是使用 js 插入到 attachmentContainer 中的，并且在 dc 代码中这个附件元素和这个容器会放在另一个 react 树中
     * 这样会导致附件元素上触发的事件无法在当前 react 树中正确被处理，当前 react 树上的组件就无法响应到这些事件，所以需要做一层转发
     * @returns
     */
    private delegateEventsToParent() {
        const container = this.attachmentContainer;
        const parent = container?.parentElement;
        if (!container || !parent) {
            return;
        }
        container.addEventListener('mousedown', this.handleContainerMouseDown);
    }

    private removeContainerEvent() {
        if (!this.attachmentContainer) {
            return;
        }
        this.attachmentContainer.removeEventListener('mousedown', this.handleContainerMouseDown);
    }

    private handleContainerMouseDown = (event: MouseEvent) => {
        this.attachmentContainer?.parentElement?.dispatchEvent(new MouseEvent('mousedown', event));
    };

    private getAttachmentManager() {
        const appServices = this.getAppServices();
        return appServices.viewableServiceLayer?.attachmentManager;
    }

    private getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }
}
